import { CheckCheck, ChevronUp, Eye, List, Loader2, X } from "lucide-react";
import { useState, type ReactNode } from "react";

import {
  SELECT_ALL_CAP,
  selectionSummary,
  type Selection,
} from "@/components/table/selection";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetDescription,
  SheetPanel,
  SheetTitle,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

/**
 * The bar a list shows once something is selected (#634): how many, where
 * they are, the way to see only them, and the actions.
 *
 * At the bottom of the list on a desktop; on a phone a bottom sheet over the
 * list, above the home indicator. When every row on the page is ticked and
 * the filter holds more, it offers "Select all 78 that match", capped at
 * `SELECT_ALL_CAP` for write actions and saying so.
 */
export function SelectionBar<T>({
  selection,
  onPage,
  notInView,
  pageFullySelected,
  matching,
  onSelectAllMatching,
  noun,
  actions,
  note,
}: {
  selection: Selection<T>;
  /** How many of the selection are on the page on screen. */
  onPage: number;
  /** How many of the selection the filter in force would not show. */
  notInView: number;
  /** Every selectable row on the page is ticked. */
  pageFullySelected: boolean;
  /** How many rows the filter holds, when known. */
  matching: number | null;
  /** Fetches up to `SELECT_ALL_CAP` matching rows and selects them. */
  onSelectAllMatching?: () => Promise<void>;
  /** "orders", for "Select all 78 orders that match". */
  noun: string;
  actions?: ReactNode;
  /** What the last action came to: a report, progress, a refusal. */
  note?: ReactNode;
}) {
  const isMobile = useIsMobile();
  const [loadingAll, setLoadingAll] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  if (selection.size === 0 && !note) return null;

  const offerAll =
    !selection.viewing &&
    pageFullySelected &&
    onSelectAllMatching !== undefined &&
    matching !== null &&
    matching > selection.size;
  const willSelect = matching === null ? 0 : Math.min(matching, SELECT_ALL_CAP);
  const selectAllLabel =
    matching === null
      ? ""
      : `Select all ${willSelect.toLocaleString("en-IN")} ${noun} that match${
          matching > SELECT_ALL_CAP
            ? ` (the first ${String(SELECT_ALL_CAP)}; actions run on at most ${String(SELECT_ALL_CAP)} at once)`
            : ""
        }`;
  function selectAll() {
    if (!onSelectAllMatching) return;
    setLoadingAll(true);
    void onSelectAllMatching().finally(() => {
      setLoadingAll(false);
    });
  }

  /*
    A phone gets one slim line (#647): how many, then one Actions button that
    opens a sheet holding every action, "Select all N", View and Clear. The
    old bar stacked a summary, View and ✕, and a sideways row of actions,
    a third of the screen, with "Select all" cut off and actions off the edge.
  */
  if (isMobile) {
    return (
      <>
        {/* Keeps the list's last row clear of the bar. */}
        <div aria-hidden className="h-16 shrink-0" />
        <div
          role="region"
          aria-label="Selection"
          className="bg-background fixed inset-x-0 bottom-0 z-30 border-t px-4 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-[0_-4px_12px_-6px_rgb(0_0_0_/_0.12)]"
        >
          {selection.size > 0 ? (
            <div className="flex min-h-11 items-center gap-2">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-11 shrink-0"
                aria-label="Clear the selection"
                onClick={selection.clear}
              >
                <X />
              </Button>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold tabular-nums">
                {selection.size.toLocaleString("en-IN")} selected
              </span>
              <Button
                type="button"
                className="min-h-11 shrink-0"
                onClick={() => {
                  setSheetOpen(true);
                }}
              >
                Actions
                <ChevronUp />
              </Button>
            </div>
          ) : null}
          {note ? (
            <div className={cn(selection.size > 0 && "mt-1 border-t pt-2")}>
              {note}
            </div>
          ) : null}
        </div>

        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetPanel
            side="bottom"
            className="h-auto max-h-[85svh] overflow-y-auto px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]"
          >
            <div
              aria-hidden
              className="bg-muted-foreground/30 mx-auto mb-3 h-1 w-9 rounded-full"
            />
            <SheetTitle className="text-base font-semibold tabular-nums">
              {selection.size.toLocaleString("en-IN")} {noun} selected
            </SheetTitle>
            {/* Where they are, when that says more than the count. */}
            <SheetDescription
              className={cn(
                "text-muted-foreground mt-0.5 text-sm",
                onPage === selection.size && notInView === 0 && "sr-only",
              )}
            >
              {selectionSummary({
                selected: selection.size,
                onPage,
                notInView,
              })}
            </SheetDescription>

            {actions ? (
              // Each action full width, one under another; a menu's own
              // wrapper (Change stage) stretches its button too.
              <div className="mt-4 flex flex-col gap-2 [&>*]:min-h-11 [&>*]:w-full [&>*]:justify-start [&>div>button]:min-h-11 [&>div>button]:flex-1 [&>div>button]:justify-start">
                {actions}
              </div>
            ) : null}

            <div className="mt-4 flex flex-col gap-1 border-t pt-3">
              {offerAll ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto min-h-11 justify-start text-left whitespace-normal"
                  disabled={loadingAll}
                  onClick={selectAll}
                >
                  {loadingAll ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <CheckCheck />
                  )}
                  {selectAllLabel}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                className="min-h-11 justify-start"
                onClick={() => {
                  selection.setViewing(!selection.viewing);
                  setSheetOpen(false);
                }}
              >
                {selection.viewing ? <List /> : <Eye />}
                {selection.viewing ? "Back to the list" : "View selected"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="min-h-11 justify-start"
                onClick={() => {
                  selection.clear();
                  setSheetOpen(false);
                }}
              >
                <X />
                Clear the selection
              </Button>
            </div>
          </SheetPanel>
        </Sheet>
      </>
    );
  }

  return (
    <div
      role="region"
      aria-label="Selection"
      className="bg-background shrink-0 border-t px-4 py-2.5 shadow-[0_-4px_12px_-6px_rgb(0_0_0_/_0.12)]"
    >
      {selection.size > 0 ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-sm font-semibold tabular-nums">
            {selectionSummary({
              selected: selection.size,
              onPage,
              notInView,
            })}
          </span>
          {offerAll ? (
            <Button
              type="button"
              size="sm"
              variant="link"
              className="h-auto px-0"
              disabled={loadingAll}
              onClick={selectAll}
            >
              {loadingAll ? <Loader2 className="animate-spin" /> : null}
              {selectAllLabel}
            </Button>
          ) : null}
          <div className="order-last flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                selection.setViewing(!selection.viewing);
              }}
            >
              {selection.viewing ? <List /> : <Eye />}
              {selection.viewing ? "Back to the list" : "View selected"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label="Clear the selection"
              onClick={selection.clear}
            >
              <X />
              Clear
            </Button>
          </div>
          {actions ? (
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {actions}
            </div>
          ) : null}
        </div>
      ) : null}
      {note ? (
        <div className={cn(selection.size > 0 && "mt-2 border-t pt-2")}>
          {note}
        </div>
      ) : null}
    </div>
  );
}
